import React, { useState } from 'react';
import {
  FaTimes,
  FaCheckCircle,
  FaMotorcycle,
  FaStore,
  FaClock,
  FaPhoneAlt,
} from 'react-icons/fa';
import { useThemeColors } from '../hooks/useThemeColors';

const OrderTracker = ({ isOpen, onClose, darkMode }) => {
  const [orderId, setOrderId] = useState('');
  const [trackingInfo, setTrackingInfo] = useState(null);
  const [error, setError] = useState('');

  // Central Hook se colors nikal rahe hain
  const {
    contentBg,
    cardBg,
    textColor,
    subTextColor,
    borderColor,
    headerBg,
    isDark,
  } = useThemeColors(darkMode);

  if (!isOpen) return null;

  const sampleOrder = {
    id: '123456',
    status: 'On the way',
    estimatedDelivery: '30-45 mins',
    items: [
      { name: 'Zinger Burger', quantity: 2 },
      { name: 'Fries', quantity: 1, size: 'Large' },
      { name: 'Pepsi', quantity: 1, size: 'Medium' },
    ],
    store: 'Downtown Branch',
    storePhone: '+92 300 1234567',
    rider: 'Ahmed Khan',
    riderPhone: '+92 312 9876543',
  };

  const handleTrackOrder = (e) => {
    e.preventDefault();
    setError('');

    if (!orderId.trim()) {
      setError('Please enter an order ID');
      return;
    }

    setTimeout(() => {
      if (orderId.toUpperCase() === sampleOrder.id) {
        setTrackingInfo(sampleOrder);
      } else {
        setError('Order not found. Please check your order ID.');
        setTrackingInfo(null);
      }
    }, 500);
  };

  const getStatusProgress = () => {
    if (!trackingInfo) return 0;
    switch (trackingInfo.status) {
      case 'Preparing':
        return 33;
      case 'On the way':
        return 66;
      case 'Delivered':
        return 100;
      default:
        return 0;
    }
  };

  return (
    <div
      className="order-tracker-container"
      style={{
        backgroundColor: contentBg,
        zIndex: 1250, // Sidebar ke uper rakha taake clear dikhay
        color: textColor,
        borderLeft: `1px solid ${borderColor}`,
        position: 'fixed',
        right: 0,
        top: '65px', // Navbar ki height ke mutabiq
        height: 'calc(100vh - 65px)',
        width: '400px',
        maxWidth: '100%',
        boxShadow: '-5px 0 15px rgba(0,0,0,0.1)',
        transition: '0.3s ease',
      }}
    >
      {/* Header Section */}
      <div
        className="order-tracker-header d-flex justify-content-between align-items-center p-3 border-bottom"
        style={{ backgroundColor: headerBg, borderColor: borderColor }}
      >
        <h5 className="mb-0 fw-bold">Track Your Order</h5>
        <button
          className="btn p-2 border-0 rounded-circle d-flex align-items-center justify-content-center"
          onClick={onClose}
          style={{
            backgroundColor: isDark
              ? 'rgba(255,255,255,0.1)'
              : 'rgba(0,0,0,0.05)',
          }}
        >
          <FaTimes size={18} className="text-danger" />
        </button>
      </div>

      <div className="order-tracker-content p-4 overflow-auto h-100 pb-5">
        {!trackingInfo ? (
          /* TRACK ORDER FORM */
          <div className="track-order-form">
            <form onSubmit={handleTrackOrder}>
              <div className="form-group mb-4">
                <label
                  className="form-label small fw-bold mb-2"
                  style={{ color: subTextColor }}
                >
                  Order ID
                </label>
                <input
                  type="text"
                  className="form-control"
                  style={{
                    backgroundColor: isDark
                      ? 'rgba(255,255,255,0.05)'
                      : '#f8f9fa',
                    color: textColor,
                    border: `1px solid ${borderColor}`,
                    borderRadius: '12px',
                    padding: '12px',
                    boxShadow: 'none',
                  }}
                  value={orderId}
                  onChange={(e) => setOrderId(e.target.value)}
                  placeholder="Enter 6-digit ID (e.g. 123456)"
                />
                {error && (
                  <p className="text-danger small mt-2 fw-bold">{error}</p>
                )}
              </div>
              <button
                type="submit"
                className="btn btn-danger w-100 fw-bold py-3 rounded-pill shadow-sm"
              >
                Track Order
              </button>
            </form>
            <div
              className="mt-4 p-3 rounded-4"
              style={{
                backgroundColor: cardBg,
                border: `1px solid ${borderColor}`,
              }}
            >
              <p
                className="mb-0 small"
                style={{ color: subTextColor, lineHeight: '1.5' }}
              >
                💡 Tip: You can find your Order ID in the confirmation message
                or on your digital receipt.
              </p>
            </div>
          </div>
        ) : (
          /* STATUS & DETAILS VIEW */
          <div className="order-details">
            <div
              className="order-status mb-4 p-3 rounded-4 shadow-sm"
              style={{
                backgroundColor: cardBg,
                border: `1px solid ${borderColor}`,
              }}
            >
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0 fw-bold">Order #{trackingInfo.id}</h6>
                <span className="badge bg-danger rounded-pill px-3">
                  {trackingInfo.status}
                </span>
              </div>

              {/* Progress Bar Container */}
              <div
                className="progress mb-4 rounded-pill"
                style={{
                  height: '8px',
                  backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : '#e9ecef',
                }}
              >
                <div
                  className="progress-bar bg-danger rounded-pill"
                  role="progressbar"
                  style={{
                    width: `${getStatusProgress()}%`,
                    transition: 'width 1s ease',
                  }}
                ></div>
              </div>

              <div className="progress-steps d-flex justify-content-between">
                {[
                  {
                    label: 'Placed',
                    threshold: 0,
                    icon: <FaCheckCircle size={18} />,
                  },
                  {
                    label: 'Preparing',
                    threshold: 33,
                    icon: <FaCheckCircle size={18} />,
                  },
                  {
                    label: 'On the way',
                    threshold: 66,
                    icon:
                      trackingInfo.status === 'Delivered' ? (
                        <FaCheckCircle size={18} />
                      ) : (
                        <FaMotorcycle size={18} />
                      ),
                  },
                ].map((step, i) => (
                  <div
                    key={i}
                    className={`text-center ${
                      getStatusProgress() >= step.threshold
                        ? 'text-danger'
                        : 'text-muted opacity-50'
                    }`}
                  >
                    {step.icon}
                    <div
                      style={{ fontSize: '0.7rem', fontWeight: 'bold' }}
                      className="mt-1"
                    >
                      {step.label}
                    </div>
                  </div>
                ))}
              </div>

              <div
                className="mt-4 d-flex align-items-center justify-content-center py-2 border-top"
                style={{ borderColor: borderColor }}
              >
                <FaClock className="me-2 text-danger" size={14} />
                <span
                  className="small fw-medium"
                  style={{ color: subTextColor }}
                >
                  Estimated:{' '}
                  <strong style={{ color: textColor }}>
                    {trackingInfo.estimatedDelivery}
                  </strong>
                </span>
              </div>
            </div>

            {/* Summary List */}
            <div className="order-summary mb-4">
              <h6 className="fw-bold mb-3" style={{ color: textColor }}>
                Order Summary
              </h6>
              <div
                className="rounded-4 overflow-hidden border"
                style={{ borderColor: borderColor }}
              >
                {trackingInfo.items.map((item, index) => (
                  <div
                    key={index}
                    className="d-flex justify-content-between align-items-center p-3 border-bottom bg-transparent"
                    style={{ borderColor: borderColor, fontSize: '0.85rem' }}
                  >
                    <span style={{ color: textColor }}>
                      <span className="fw-bold text-danger me-2">
                        {item.quantity}x
                      </span>{' '}
                      {item.name}
                    </span>
                    {item.size && (
                      <span className="badge bg-light text-dark">
                        {item.size}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Contact Sections */}
            <div className="contact-info d-flex flex-column gap-3 pb-4">
              <div
                className="p-3 rounded-4"
                style={{
                  backgroundColor: cardBg,
                  border: `1px solid ${borderColor}`,
                }}
              >
                <h6 className="small fw-bold mb-2">
                  <FaStore className="text-danger me-2" /> Store Details
                </h6>
                <p
                  className="mb-2 small fw-medium"
                  style={{ color: textColor }}
                >
                  {trackingInfo.store}
                </p>
                <a
                  href={`tel:${trackingInfo.storePhone}`}
                  className="btn btn-sm btn-outline-danger w-100 rounded-pill small fw-bold"
                >
                  <FaPhoneAlt className="me-1" size={10} />{' '}
                  {trackingInfo.storePhone}
                </a>
              </div>

              {trackingInfo.status === 'On the way' && (
                <div
                  className="p-3 rounded-4"
                  style={{
                    backgroundColor: cardBg,
                    border: `1px solid ${borderColor}`,
                  }}
                >
                  <h6 className="small fw-bold mb-2">
                    <FaMotorcycle className="text-danger me-2" /> Rider Info
                  </h6>
                  <p
                    className="mb-2 small fw-medium"
                    style={{ color: textColor }}
                  >
                    {trackingInfo.rider}
                  </p>
                  <a
                    href={`tel:${trackingInfo.riderPhone}`}
                    className="btn btn-sm btn-danger w-100 rounded-pill small fw-bold shadow-sm"
                  >
                    <FaPhoneAlt className="me-1" size={10} /> Call Rider
                  </a>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderTracker;
